import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Button, Card, CardContent, Chip, CircularProgress, Container,
  Divider, Grid, LinearProgress, Tab, Tabs, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography, IconButton,
} from "@mui/material";
import { ArrowBack as ArrowBackIcon, Visibility as VisibilityIcon } from "@mui/icons-material";
import { useNavigate, useParams } from "react-router-dom";
import { getDifusionBatch, getDifusionBatchMensajes } from "../../services/api";
import showToast from "../../services/toast";
import MensajeLogModal from "../../components/MensajeLogModal/MensajeLogModal";

const STATUS_CONFIG = {
  en_proceso: { label: "En proceso", color: "#2196f3", bg: "#e3f2fd" },
  completado: { label: "Completado", color: "#4caf50", bg: "#e8f5e9" },
  con_errores: { label: "Con errores", color: "#ff9800", bg: "#fff3e0" },
};

const MSG_STATUS_CONFIG = {
  aceptado:  { label: "Aceptado",  color: "#9e9e9e" },
  enviado:   { label: "Enviado",   color: "#2196f3" },
  entregado: { label: "Entregado", color: "#4caf50" },
  leido:     { label: "Leído",     color: "#2e7d32" },
  fallido:   { label: "Fallido",   color: "#f44336" },
};

const TABS = [
  { value: '', label: 'Todos' },
  { value: 'aceptado', label: 'Aceptados' },
  { value: 'entregado', label: 'Entregados' },
  { value: 'leido', label: 'Leídos' },
  { value: 'fallido', label: 'Fallidos' },
];

const ITEMS_PER_PAGE = 20;

const DifusionBatchDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [batch, setBatch] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [mensajesCount, setMensajesCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tabActivo, setTabActivo] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMensajes, setLoadingMensajes] = useState(false);
  const [mensajeSeleccionado, setMensajeSeleccionado] = useState(null);

  const fetchBatch = useCallback(async () => {
    try {
      const response = await getDifusionBatch(id);
      setBatch(response.data);
    } catch (error) {
      showToast.error('Error al cargar el detalle de la difusión');
    } finally {
      setLoading(false);
    }
  }, [id]);

  const fetchMensajes = useCallback(async () => {
    setLoadingMensajes(true);
    try {
      const params = { page: currentPage };
      if (tabActivo) params.status = tabActivo;
      const response = await getDifusionBatchMensajes(id, params);
      const data = response.data;
      if (data.results) {
        setMensajes(data.results);
        setMensajesCount(data.count || 0);
      } else {
        setMensajes(Array.isArray(data) ? data : []);
        setMensajesCount(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      showToast.error('Error al cargar los mensajes');
    } finally {
      setLoadingMensajes(false);
    }
  }, [id, tabActivo, currentPage]);

  useEffect(() => { fetchBatch(); }, [fetchBatch]);
  useEffect(() => { fetchMensajes(); }, [fetchMensajes]);

  // Polling mientras en_proceso
  useEffect(() => {
    if (batch?.status === 'en_proceso') {
      const interval = setInterval(() => {
        fetchBatch();
        fetchMensajes();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [batch?.status, fetchBatch, fetchMensajes]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-PY', {
      hour: '2-digit', minute: '2-digit', second: '2-digit'
    });
  };

  const formatFullDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('es-PY', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!batch) {
    return (
      <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
        <Typography>Difusión no encontrada</Typography>
      </Container>
    );
  }

  const batchCfg = STATUS_CONFIG[batch.status] || STATUS_CONFIG.completado;
  const progreso = batch.total_destinatarios > 0
    ? Math.round((batch.total_procesados / batch.total_destinatarios) * 100)
    : 0;
  const totalPages = Math.ceil(mensajesCount / ITEMS_PER_PAGE);

  return (
    <Container style={{ marginLeft: "0", maxWidth: "100%" }}>
      <MensajeLogModal
        open={!!mensajeSeleccionado}
        mensaje={mensajeSeleccionado}
        onClose={() => setMensajeSeleccionado(null)}
      />
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/app/difusiones')}>
            Volver
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {batch.template_nombre}
          </Typography>
        </Box>
        <Chip
          label={
            batch.status === 'en_proceso'
              ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CircularProgress size={12} sx={{ color: batchCfg.color }} />
                  {batchCfg.label}
                </Box>
              : batchCfg.label
          }
          sx={{ color: batchCfg.color, bgcolor: batchCfg.bg, fontWeight: 600, px: 1 }}
        />
      </Box>

      {/* Meta info */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {batch.empresa_nombre || batch.empresa_ruc} &nbsp;|&nbsp;
        Enviado: {formatFullDate(batch.fecha_envio)}
        {batch.creado_por_nombre && ` por ${batch.creado_por_nombre}`}
      </Typography>

      {/* Barra de progreso (solo si en_proceso) */}
      {batch.status === 'en_proceso' && (
        <Card sx={{ mb: 3 }} variant="outlined">
          <CardContent>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Procesando... {batch.total_procesados} de {batch.total_destinatarios} mensajes
            </Typography>
            <LinearProgress variant="determinate" value={progreso} sx={{ height: 8, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary">{progreso}%</Typography>
          </CardContent>
        </Card>
      )}

      {/* Cards de stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total', value: batch.total_destinatarios, color: '#1976d2' },
          { label: 'Aceptados', value: batch.total_aceptados, color: '#9e9e9e' },
          { label: 'Entregados', value: batch.total_entregados, color: '#4caf50' },
          { label: 'Leídos', value: batch.total_leidos, color: '#2e7d32' },
          { label: 'Fallidos', value: batch.total_fallidos, color: '#f44336' },
        ].map(({ label, value, color }) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <Card variant="outlined" sx={{ textAlign: 'center', py: 1 }}>
              <CardContent sx={{ py: '8px !important' }}>
                <Typography variant="h5" fontWeight="bold" sx={{ color }}>
                  {value}
                </Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Divider sx={{ mb: 2 }} />

      {/* Tabs de filtro */}
      <Tabs
        value={tabActivo}
        onChange={(_, val) => { setTabActivo(val); setCurrentPage(1); }}
        sx={{ mb: 2 }}
      >
        {TABS.map(tab => (
          <Tab key={tab.value} value={tab.value} label={tab.label} />
        ))}
      </Tabs>

      {/* Tabla de mensajes */}
      {loadingMensajes ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Nombre</strong></TableCell>
                  <TableCell><strong>Teléfono</strong></TableCell>
                  <TableCell><strong>Sucursal</strong></TableCell>
                  <TableCell><strong>Estado</strong></TableCell>
                  <TableCell><strong>Entregado</strong></TableCell>
                  <TableCell><strong>Leído</strong></TableCell>
                  <TableCell><strong>Error</strong></TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {mensajes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                      No hay mensajes en esta categoría
                    </TableCell>
                  </TableRow>
                ) : mensajes.map((msg) => {
                  const statusCfg = MSG_STATUS_CONFIG[msg.status] || MSG_STATUS_CONFIG.aceptado;
                  return (
                    <TableRow key={msg.id} hover>
                      <TableCell>{msg.destinatario_nombre || '—'}</TableCell>
                      <TableCell>{msg.destinatario_telefono}</TableCell>
                      <TableCell>{msg.sucursal_codigo}</TableCell>
                      <TableCell>
                        <Chip
                          label={statusCfg.label}
                          size="small"
                          sx={{ color: statusCfg.color, bgcolor: `${statusCfg.color}1a`, fontWeight: 500 }}
                        />
                      </TableCell>
                      <TableCell>{formatDate(msg.fecha_entregado)}</TableCell>
                      <TableCell>{formatDate(msg.fecha_leido)}</TableCell>
                      <TableCell>
                        {msg.error_mensaje ? (
                          <Tooltip title={msg.error_mensaje}>
                            <Typography variant="caption" color="error" sx={{ cursor: 'help' }}>
                              {msg.error_codigo || 'Error'} ⓘ
                            </Typography>
                          </Tooltip>
                        ) : '—'}
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Ver detalle">
                          <IconButton size="small" onClick={() => setMensajeSeleccionado(msg)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, gap: 1 }}>
              <Button disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>
                Anterior
              </Button>
              <Typography sx={{ alignSelf: 'center' }}>{currentPage} / {totalPages}</Typography>
              <Button disabled={currentPage === totalPages} onClick={() => setCurrentPage(p => p + 1)}>
                Siguiente
              </Button>
            </Box>
          )}
        </>
      )}
    </Container>
  );
};

export default DifusionBatchDetalle;
